import { supabase } from './supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

export type NotificationType = 'comment' | 'share' | 'mention' | 'document_update' | 'collaboration_invite';

export interface Notification {
  id: string;
  user_id: string;
  type: NotificationType;
  title: string;
  message: string;
  document_id?: string | null;
  action_url?: string | null;
  is_read: boolean;
  metadata: Record<string, any>;
  created_at: string;
  read_at?: string | null;
}

export interface NotificationPreferences {
  user_id: string;
  email_comments: boolean;
  email_shares: boolean;
  email_mentions: boolean;
  email_updates: boolean;
  push_enabled: boolean;
  sound_enabled: boolean;
  updated_at: string;
}

class NotificationService {
  private channel: RealtimeChannel | null = null;
  private notificationCallbacks: Set<(notifications: Notification[]) => void> = new Set();
  private currentNotifications: Notification[] = [];
  private unreadCount: number = 0;
  private countCallbacks: Set<(count: number) => void> = new Set();

  async subscribe(userId: string, onNotificationsChange: (notifications: Notification[]) => void) {
    this.notificationCallbacks.add(onNotificationsChange);

    await this.loadNotifications(userId);

    if (!this.channel) {
      this.channel = supabase.channel(`notifications:${userId}`);

      this.channel
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userId}`
          },
          () => {
            this.loadNotifications(userId);
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log('Subscribed to notifications');
          }
        });
    }
  }

  unsubscribe(callback: (notifications: Notification[]) => void) {
    this.notificationCallbacks.delete(callback);

    if (this.notificationCallbacks.size === 0 && this.channel) {
      this.channel.unsubscribe();
      this.channel = null;
    }
  }

  subscribeToCount(callback: (count: number) => void) {
    this.countCallbacks.add(callback);
    callback(this.unreadCount);
  }

  unsubscribeFromCount(callback: (count: number) => void) {
    this.countCallbacks.delete(callback);
  }

  private async loadNotifications(userId: string) {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      this.currentNotifications = data || [];
      this.unreadCount = this.currentNotifications.filter(n => !n.is_read).length;

      this.notifyCallbacks();
      this.notifyCountCallbacks();
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  }

  private notifyCallbacks() {
    this.notificationCallbacks.forEach(callback => {
      callback([...this.currentNotifications]);
    });
  }

  private notifyCountCallbacks() {
    this.countCallbacks.forEach(callback => {
      callback(this.unreadCount);
    });
  }

  async markAsRead(notificationId: string, userId: string) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId)
        .eq('user_id', userId);

      if (error) throw error;

      await this.loadNotifications(userId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  async markAllAsRead(userId: string) {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', userId)
        .eq('is_read', false);

      if (error) throw error;

      await this.loadNotifications(userId);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  async deleteNotification(notificationId: string, userId: string) {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId)
        .eq('user_id', userId);

      if (error) throw error;

      await this.loadNotifications(userId);
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  async clearAll(userId: string) {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;

      await this.loadNotifications(userId);
    } catch (error) {
      console.error('Error clearing notifications:', error);
      throw error;
    }
  }

  async createNotification(
    userId: string,
    type: NotificationType,
    title: string,
    message: string,
    documentId?: string,
    actionUrl?: string,
    metadata?: Record<string, any>
  ) {
    try {
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          type,
          title,
          message,
          document_id: documentId || null,
          action_url: actionUrl || null,
          metadata: metadata || {}
        });

      if (error) throw error;

      await this.loadNotifications(userId);
    } catch (error) {
      console.error('Error creating notification:', error);
      throw error;
    }
  }

  async getPreferences(userId: string): Promise<NotificationPreferences | null> {
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        const defaultPreferences = {
          user_id: userId,
          email_comments: true,
          email_shares: true,
          email_mentions: true,
          email_updates: false,
          push_enabled: true,
          sound_enabled: true
        };

        const { data: newData, error: insertError } = await supabase
          .from('notification_preferences')
          .insert(defaultPreferences)
          .select()
          .single();

        if (insertError) throw insertError;

        return newData;
      }

      return data;
    } catch (error) {
      console.error('Error getting notification preferences:', error);
      return null;
    }
  }

  async updatePreferences(userId: string, preferences: Partial<NotificationPreferences>) {
    try {
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: userId,
          ...preferences
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }

  getUnreadCount(): number {
    return this.unreadCount;
  }

  getNotifications(): Notification[] {
    return [...this.currentNotifications];
  }
}

export const notificationService = new NotificationService();
